import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { colors } from "@ippon/shared-components";

// カスタムフォーム設定 (CustomForm/index.tsx) と同じアコーディオン外観を踏襲する。
const ACCORDION_SX = {
  border: "1px solid #d9d9d9",
  borderRadius: "10px",
  boxShadow: "none",
  "&:before": { display: "none" },
  "&.Mui-expanded": { borderRadius: "10px" },
} as const;

const ACCORDION_SUMMARY_SX = {
  px: 2,
  py: 0,
  minHeight: 50,
  borderRadius: "10px",
  "& .MuiAccordionSummary-content": { margin: "7px 0" },
  "&.Mui-expanded": { borderRadius: "10px 10px 0 0", minHeight: 50 },
  "&.Mui-expanded .MuiAccordionSummary-content": { margin: "7px 0" },
} as const;

interface PulldownAccordionProps {
  title: string;
  /** ヘッダー右の「N件」バッジに出す件数 */
  count: number;
  expanded: boolean;
  onToggle: (next: boolean) => void;
  children: React.ReactNode;
}

/**
 * カスタムプルダウンの各マスタを束ねるアコーディオン。
 * カスタムフォーム設定ページのアコーディオン (自前 Chevron + 件数バッジ) と同じ見た目。
 */
const PulldownAccordion: React.FC<PulldownAccordionProps> = ({
  title,
  count,
  expanded,
  onToggle,
  children,
}) => (
  <Accordion
    sx={ACCORDION_SX}
    expanded={expanded}
    onChange={(_, next) => onToggle(next)}
  >
    <AccordionSummary expandIcon={null} sx={ACCORDION_SUMMARY_SX}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            cursor: "pointer",
            backgroundColor: expanded ? "#DBF8FF" : "transparent",
            color: expanded ? "#0E9DBC" : "#3e3e3e",
            transition: "all 0.2s",
            flexShrink: 0,
            "&:hover": {
              backgroundColor: "#DBF8FF",
              "& svg": { color: "#0E9DBC" },
            },
          }}
        >
          <ChevronRightIcon
            sx={{
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              fontSize: 20,
              color: "inherit",
              transition: "transform 0.2s ease",
            }}
          />
        </Box>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ flex: 1, minWidth: 0, gap: "8px" }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#3E3E3E" }}>
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: colors.text.secondary,
              backgroundColor: "#F5F5F5",
              px: 1,
              py: 0.25,
              borderRadius: 999,
              flexShrink: 0,
            }}
          >
            {count}件
          </Typography>
        </Stack>
      </Stack>
    </AccordionSummary>
    <AccordionDetails sx={{ borderTop: "1px solid #E0E0E0", p: 2 }}>
      {children}
    </AccordionDetails>
  </Accordion>
);

export default PulldownAccordion;
